import { Button } from '@affine/component';
import { AuthInput } from '@affine/component/auth-components';
import {
  AuthService,
  CaptchaService,
  type Server,
} from '@affine/core/modules/cloud';
import { MAGICNOTES_SERVER_NAME } from '@affine/core/modules/cloud/magicnotes';
import { UserFriendlyError } from '@affine/error';
import { FrameworkScope, useLiveData } from '@toeverything/infra';
import { useEffect, useRef, useState } from 'react';

import { Captcha } from './captcha';
import * as styles from './style.css';

function setupError(error: unknown) {
  const friendly = UserFriendlyError.fromAny(error);
  if (friendly.is('SIGN_UP_FORBIDDEN')) {
    return 'This email is not approved for registration. Ask your administrator for an invitation.';
  }
  if (friendly.is('INVALID_EMAIL_TOKEN')) {
    return 'The verification code is incorrect or expired. Try again or request a new code.';
  }
  if (friendly.is('WRONG_SIGN_IN_METHOD')) {
    return 'This account already has a password. Go back to sign in.';
  }
  return friendly.message;
}

// Invitation links identify a destination, not the person opening them. A fresh
// mailbox code is required before the server can create the first password.
export function InvitationSetup({
  server,
  email,
  onEmailChange,
  initialToken,
  redirectUrl,
}: {
  server?: Server;
  email: string;
  onEmailChange: (email: string) => void;
  initialToken?: string;
  redirectUrl?: string;
}) {
  const auth = server?.scope.get(AuthService);
  const captcha = server?.scope.get(CaptchaService);
  const needCaptcha = useLiveData(captcha?.needCaptcha$);
  const verifyToken = useLiveData(captcha?.verifyToken$);
  const challenge = useLiveData(captcha?.challenge$);
  const requirements = useLiveData(server?.credentialsRequirement$)?.password;
  const [sent, setSent] = useState(!!initialToken && !!email);
  const [code, setCode] = useState(initialToken ?? '');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendCode = async () => {
    if (!auth || submitting.current || (needCaptcha && !verifyToken)) return;
    submitting.current = true;
    setLoading(true);
    setError(undefined);
    try {
      await auth.sendInvitationCode(
        email.trim(),
        verifyToken,
        challenge,
        redirectUrl
      );
      setSent(true);
      setCode('');
      setCooldown(60);
    } catch (error) {
      setError(setupError(error));
    } finally {
      captcha?.revalidate();
      submitting.current = false;
      setLoading(false);
    }
  };

  const finish = async () => {
    if (!auth || submitting.current || !requirements) return;
    setError(undefined);
    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (
      password.length < requirements.minLength ||
      password.length > requirements.maxLength
    ) {
      setError(
        `Use ${requirements.minLength}–${requirements.maxLength} characters for your password.`
      );
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      await auth.completeInvitation(email.trim(), code, password);
    } catch (error) {
      setError(setupError(error));
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <form
      className={styles.credentialsForm}
      onSubmit={event => {
        event.preventDefault();
        (sent ? finish() : sendCode()).catch(console.error);
      }}
    >
      <p>
        {sent
          ? `Enter the code sent to ${email}, then choose your password.`
          : 'Use the email address your administrator invited. We’ll send a code to verify it.'}
      </p>
      <AuthInput
        label="Server"
        aria-label="Server"
        value={MAGICNOTES_SERVER_NAME}
        readOnly
        disabled
        tabIndex={-1}
      />
      <AuthInput
        label="Email"
        aria-label="Email"
        type="email"
        autoComplete="username"
        required
        value={email}
        readOnly={sent}
        disabled={loading}
        onChange={onEmailChange}
      />
      {sent && (
        <>
          <AuthInput
            label="Verification code"
            aria-label="Verification code"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={setCode}
          />
          <AuthInput
            label="New password"
            aria-label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={requirements?.minLength}
            maxLength={requirements?.maxLength}
            value={password}
            onChange={setPassword}
          />
          <AuthInput
            label="Confirm password"
            aria-label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={setConfirmation}
          />
          {requirements && (
            <p>
              Use {requirements.minLength}–{requirements.maxLength} characters
              for your password.
            </p>
          )}
        </>
      )}
      {server && needCaptcha && (
        <FrameworkScope scope={server.scope}>
          <Captcha />
        </FrameworkScope>
      )}
      {error && (
        <div role="alert" className={styles.signInError}>
          {error}
        </div>
      )}
      <Button
        variant="primary"
        size="extraLarge"
        block
        loading={loading}
        disabled={
          !auth ||
          loading ||
          !email.trim() ||
          (sent
            ? !requirements ||
              !/^[0-9]{6}$/.test(code) ||
              !password ||
              !confirmation
            : !!needCaptcha && !verifyToken)
        }
      >
        {sent ? 'Create password and continue' : 'Send verification code'}
      </Button>
      {sent && (
        <>
          <Button
            variant="plain"
            disabled={
              loading || cooldown > 0 || (!!needCaptcha && !verifyToken)
            }
            onClick={event => {
              event.preventDefault();
              sendCode().catch(console.error);
            }}
          >
            {cooldown ? `Resend code in ${cooldown}s` : 'Resend code'}
          </Button>
          <Button
            variant="plain"
            disabled={loading}
            onClick={event => {
              event.preventDefault();
              setSent(false);
              setCode('');
              setPassword('');
              setConfirmation('');
              setError(undefined);
            }}
          >
            Use a different email
          </Button>
        </>
      )}
    </form>
  );
}
